import React, { useMemo, useState } from 'react';
import { ICompany, ProjectArchiveSummary } from '../../api-client/autogenerated';
import { getFileUrlById } from '../../models/api/files';
import { getProjectArchiveShareRecipients, shareProjectArchiveZip } from '../../models/api/project';
import { describeArchiveSelection } from '../custom-components/document-type-utils';
import EmailUsersDialog from '../design/EmailUsersDialog';
import ExportContentsDialog from './ExportContentsDialog';
import ExportLogTable, { ExportLogEntry } from './ExportLogTable';

type ProjectArchivesListProps = {
  archives: ProjectArchiveSummary[];
  isLoading: boolean;
  projectId: string;
  projectName: string;
  /** How many document types the picker offers, so a full selection can be summarised as one word. */
  totalSelectableDocumentTypes: number;
};

type ArchiveLogEntry = ExportLogEntry & { archive: ProjectArchiveSummary };

/**
 * The project's exports. An export outlives the dialog that started it, so this is how someone
 * picks a finished one back up rather than waiting on the page for it.
 */
export default function ProjectArchivesList(props: ProjectArchivesListProps) {
  const { archives, isLoading, projectId, projectName, totalSelectableDocumentTypes } = props;

  const [contentsArchive, setContentsArchive] = useState<ProjectArchiveSummary>();
  const [shareArchive, setShareArchive] = useState<ProjectArchiveSummary>();
  const [shareCompanies, setShareCompanies] = useState<ICompany[]>();

  const entries = useMemo<ArchiveLogEntry[]>(
    () =>
      archives.map((archive) => ({
        id: archive.jobStatusId,
        generatedOn: archive.requestedOn,
        generatedByUserName: archive.requestedByUserName,
        status: archive.status,
        percentComplete: archive.percentComplete,
        fileId: archive.fileId,
        error: archive.error,
        isCurrent: archive.isCurrent,
        archive,
      })),
    [archives],
  );

  return (
    <>
      <ExportLogTable
        entries={entries}
        isLoading={isLoading}
        emptyMessage="This project has not been exported yet."
        staleTooltip="Not current. Activity detected after this export."
        describeContents={({ archive }) =>
          describeArchiveSelection(archive, totalSelectableDocumentTypes)
        }
        onShowContents={({ archive }) => setContentsArchive(archive)}
        onShare={({ archive }) => {
          // The project's users cannot come from the loaded project here: an export only exists on
          // an archived project, and those never load.
          getProjectArchiveShareRecipients(projectId)
            .then(setShareCompanies)
            .catch(() => setShareCompanies([]));
          setShareArchive(archive);
        }}
      />

      <ExportContentsDialog
        archive={contentsArchive}
        closeDialog={() => setContentsArchive(undefined)}
      />
      <EmailUsersDialog
        dialogOpen={!!shareArchive}
        closeDialog={() => setShareArchive(undefined)}
        selectableCompanies={shareCompanies}
        projectName={projectName}
        onSubmit={async (emails, note) => {
          if (!shareArchive?.fileId || emails.length === 0) return;
          await shareProjectArchiveZip(projectId, shareArchive.fileId, emails, note);
        }}
        getLink={async () => {
          if (!shareArchive?.fileId) return '';
          const { downloadLink } = await getFileUrlById(shareArchive.fileId, true);
          return downloadLink ?? '';
        }}
        defaultSubject={`Export of ${projectName}`}
      />
    </>
  );
}
